import { useRef } from 'react'
import CLUB from '../config/club.js'

// The awards-ceremony reveal for a "Winner of the Hour" (TV wall only).
//   phase 'announce' → dark spotlight stage, gold shimmer, PBCC pine logo, a
//     build-up beat ("Top Photo of the Hour… and it goes to…").
//   phase 'reveal'   → the winner bursts in inside a glowing gold frame with a
//     trophy, a confetti/sparkle burst, and the team last names in gold.
// Pure CSS animation — no deps. Driven by PhotoSlideshow (which owns timing).

const AWARD_KIND = {
  photo: { title: 'Top Photo of the Hour', icon: '🏆' },
  video: { title: 'Top Video of the Hour', icon: '🎬' },
}

const AMBIENT = Array.from({ length: 20 }, (_, i) => i) // floating shimmer specks
const BURST = Array.from({ length: 30 }, (_, i) => i)   // confetti burst pieces
const VIDEO_PLAYS = 2 // a winning video plays through this many times, then done

// `onVideoDone` fires once a winning video has played through VIDEO_PLAYS times
// (PhotoSlideshow then advances to the next hour). Photo winners are timed by
// PhotoSlideshow instead, so they ignore this callback.
export default function AwardReveal({ award, who, embedded = false, videoUnmuted = false, onVideoDone }) {
  const meta = AWARD_KIND[award.kind] || AWARD_KIND.photo
  const announcing = award.phase === 'announce'
  const p = award.photo
  const plays = useRef(0)

  const handleVideoEnd = (e) => {
    plays.current += 1
    if (plays.current < VIDEO_PLAYS) {
      const el = e.currentTarget
      try { el.currentTime = 0; el.play() } catch { /* autoplay retry no-op */ }
    } else {
      onVideoDone?.()
    }
  }

  return (
    <div
      className={`${embedded ? 'h-full' : 'min-h-screen'} relative overflow-hidden`}
      style={{ background: 'radial-gradient(circle at 50% 40%, #163821 0%, #0a1710 46%, #000 100%)' }}>

      {/* ambient gold shimmer specks (always present, subtle) */}
      {AMBIENT.map((i) => (
        <span key={i} className="ar-speck" style={{
          left: `${(i * 137) % 100}%`, top: `${(i * 71 + 13) % 100}%`,
          animationDelay: `${(i % 10) * 300}ms`, animationDuration: `${3200 + (i % 5) * 600}ms`,
        }} />
      ))}

      {announcing ? (
        <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-10">
          <img src={CLUB.logoLight} alt={CLUB.name} className="ar-logo" style={{ height: embedded ? '84px' : '132px', objectFit: 'contain', marginBottom: '2rem' }} />
          <div className="ar-line1 uppercase tracking-[0.3em] text-sm md:text-xl text-white/80">{award.label}</div>
          <div className="ar-line1 uppercase tracking-widest text-3xl md:text-6xl font-light text-white mt-3">
            {meta.icon} {meta.title}
          </div>
          <div className="ar-line2 text-2xl md:text-4xl mt-9" style={{ color: '#F5C542' }}>…and it goes to…</div>
        </div>
      ) : (
        <div className="absolute inset-0 flex flex-col items-center justify-center px-6">
          {/* spotlight cone behind the frame */}
          <div className="ar-spotlight" />
          <div className="ar-frame relative" style={{
            padding: '12px', borderRadius: '16px',
            background: 'linear-gradient(135deg, #FCE38A 0%, #F5C542 45%, #B8860B 100%)',
            boxShadow: '0 0 70px 10px rgba(245,197,66,0.55), inset 0 0 12px rgba(255,255,255,0.6)',
          }}>
            <div className="overflow-hidden rounded-lg bg-black">
              {award.kind === 'video' ? (
                <video src={p.url} poster={p.posterUrl || undefined}
                  className="object-contain" style={{ maxWidth: '78vw', maxHeight: '56vh' }}
                  autoPlay muted={!videoUnmuted} playsInline onEnded={handleVideoEnd} />
              ) : (
                <img src={p.url} alt={p.caption || ''} className="object-contain" style={{ maxWidth: '78vw', maxHeight: '56vh' }} />
              )}
            </div>
            <div className="ar-trophy absolute -top-7 -left-7 select-none" style={{ fontSize: '3.4rem', filter: 'drop-shadow(0 3px 6px rgba(0,0,0,0.6))' }}>🏆</div>
          </div>

          {/* confetti / sparkle burst from center */}
          <div className="ar-burst-origin">
            {BURST.map((i) => {
              const ang = (i / BURST.length) * Math.PI * 2
              const dist = 200 + (i % 5) * 60
              return (
                <span key={i} className="ar-confetti" style={{
                  '--x': `${Math.cos(ang) * dist}px`, '--y': `${Math.sin(ang) * dist}px`,
                  animationDelay: `${(i % 6) * 45}ms`,
                  backgroundColor: i % 3 === 0 ? '#FDF0B0' : i % 3 === 1 ? '#F5C542' : '#FFFFFF',
                  borderRadius: i % 2 ? '50%' : '2px',
                }} />
              )
            })}
          </div>

          <div className="ar-name text-center mt-8">
            <div className="uppercase tracking-[0.25em] text-sm md:text-lg" style={{ color: '#F5C542' }}>{meta.icon} {meta.title}</div>
            <div className="text-4xl md:text-6xl font-extrabold text-white mt-1" style={{ textShadow: '0 2px 22px rgba(245,197,66,0.55)' }}>{who}</div>
          </div>
        </div>
      )}

      <style>{`
        .ar-speck { position: absolute; width: 4px; height: 4px; border-radius: 50%;
          background: radial-gradient(circle, #FDF0B0, #F5C542); opacity: 0;
          animation-name: arTwinkle; animation-iteration-count: infinite; animation-timing-function: ease-in-out; }
        @keyframes arTwinkle {
          0% { opacity: 0; transform: translateY(6px) scale(0.6); }
          50% { opacity: 0.85; transform: translateY(-6px) scale(1); }
          100% { opacity: 0; transform: translateY(-18px) scale(0.6); } }

        .ar-logo { opacity: 0; animation: arLogoPop 900ms cubic-bezier(0.2,0.8,0.2,1) forwards; }
        @keyframes arLogoPop { 0% { opacity: 0; transform: scale(0.7) translateY(10px); } 100% { opacity: 0.95; transform: scale(1) translateY(0); } }

        .ar-line1 { opacity: 0; animation: arFadeUp 800ms ease-out 350ms forwards; }
        .ar-line2 { opacity: 0; animation: arFadeUp 900ms ease-out 1500ms forwards; }
        @keyframes arFadeUp { from { opacity: 0; transform: translateY(14px); } to { opacity: 1; transform: translateY(0); } }

        .ar-spotlight { position: absolute; top: -10%; left: 50%; width: 120vw; height: 90vh;
          transform: translateX(-50%);
          background: radial-gradient(ellipse at center, rgba(245,197,66,0.16) 0%, rgba(245,197,66,0) 62%);
          opacity: 0; animation: arFade 700ms ease-out forwards; pointer-events: none; }
        @keyframes arFade { to { opacity: 1; } }

        .ar-frame { opacity: 0; transform-origin: center;
          animation: arBurstIn 900ms cubic-bezier(0.16,1.1,0.3,1) forwards, arGlow 2600ms ease-in-out 900ms infinite; }
        @keyframes arBurstIn { 0% { opacity: 0; transform: scale(0.2) rotate(-6deg); } 60% { opacity: 1; transform: scale(1.06) rotate(1.5deg); } 100% { opacity: 1; transform: scale(1) rotate(0); } }
        @keyframes arGlow {
          0%,100% { box-shadow: 0 0 60px 8px rgba(245,197,66,0.45), inset 0 0 12px rgba(255,255,255,0.6); }
          50% { box-shadow: 0 0 95px 16px rgba(245,197,66,0.75), inset 0 0 12px rgba(255,255,255,0.7); } }

        .ar-trophy { opacity: 0; animation: arTrophy 700ms cubic-bezier(0.2,0.9,0.2,1.2) 500ms forwards; }
        @keyframes arTrophy { 0% { opacity: 0; transform: scale(0) rotate(-30deg); } 100% { opacity: 1; transform: scale(1) rotate(-12deg); } }

        .ar-burst-origin { position: absolute; top: 44%; left: 50%; width: 0; height: 0; }
        .ar-confetti { position: absolute; width: 10px; height: 10px; opacity: 0;
          animation: arConfetti 1100ms ease-out forwards; }
        @keyframes arConfetti {
          0% { opacity: 1; transform: translate(0,0) scale(0.3); }
          100% { opacity: 0; transform: translate(var(--x), var(--y)) scale(1); } }

        .ar-name { opacity: 0; animation: arFadeUp 800ms ease-out 450ms forwards; }
      `}</style>
    </div>
  )
}
